import { formatDate } from '@/utils/format';

export interface TranscriptionWord {
  text: string;
  start: number;
  end: number;
  type: string;
  speaker_id?: string;
}

export interface TranscriptionPhrase {
  text: string;
  start: number;
  end: number;
  speaker_id?: string;
}

export interface TranscriptionChunkMetadata {
  chunk_id: string;
  chunk_index: number;
  start_time: number;
  end_time: number;
  elevenlabs_request_id: string;
  status: string;
  error?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

// Defined here (before MediaTranscriptionMetadata) so the metadata
// interface can reference it without forward-declaration lint warnings.
// The full job-status interface used by the polling path lives lower.
export type AnalysisJobStatus =
  | 'queued'
  | 'running'
  | 'partial_success'
  | 'succeeded'
  | 'failed';

export interface MediaTranscriptionAIAnalysisSummary {
  latest_status?: AnalysisJobStatus;
  latest_job_id?: string;
  current_step?: string;
  // 0–100 (UI divides by 10 for the badge)
  latest_score?: number;
  products_count?: number;
  updated_at?: string;
  completed_at?: string;
}

export interface MediaTranscriptionMetadata {
  _id: string;
  media_id: string;
  plugin_user_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_chunks: number;
  completed_chunks: number;
  failed_chunks: number;
  chunks: TranscriptionChunkMetadata[];
  full_text?: string;
  total_duration: number;
  error?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  // Denormalized AI summary written by the agent-controller. Read this
  // for the report tab's top stepper; full payload still lives in
  // mediaTranscriptionAnalysis (fetched only when the user opens the card).
  ai_analysis?: MediaTranscriptionAIAnalysisSummary;
}

export interface MediaTranscriptionDetails {
  _id: string;
  media_id: string;
  chunk_id: string;
  chunk_index: number;
  start_time: number;
  end_time: number;
  text: string;
  words?: TranscriptionWord[];
  phrases?: TranscriptionPhrase[];
  language_code?: string;
  language_probability?: number;
  created_at: string;
  updated_at: string;
}

export interface GetTranscriptionRequest {
  media_id: string;
}

export interface GetTranscriptionResponse {
  metadata: MediaTranscriptionMetadata | null;
  details?: MediaTranscriptionDetails[];
}

export interface TranscribeVideoRequest {
  video_url: string;
  media_id: string;
}

export interface TranscribeVideoResponse {
  transcription_id: string;
  text: string;
  status: string;
  elevenlabs_request_id?: string;
}

export interface InstagramMedia {
  id: string;
  caption?: string;
  media_type: string;
  media_url?: string | null;
  permalink: string;
  thumbnail_url?: string;
  timestamp: string;
  username: string;
}

export interface InstagramMediaPaging {
  cursors: {
    before?: string;
    after?: string;
  };
  next?: string;
  previous?: string;
}

export interface InstagramMediaResponse {
  data: InstagramMedia[];
  paging?: InstagramMediaPaging;
}

export function isVideoMedia(media: InstagramMedia): boolean {
  return media.media_type === 'VIDEO' || media.media_type === 'REELS';
}

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return formatDate(date, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── AI analysis (status + outputs from agent-controller) ─────────────────
// `AnalysisJobStatus` is declared above (needed by MediaTranscriptionMetadata
// for the denormalized stepper summary).

export interface MediaTranscriptionJobStatus {
  media_id: string;
  job_id: string;
  workflow_name: string;
  workflow_version: string;
  status: AnalysisJobStatus;
  current_step?: string | null;
  step_states?: Record<string, string>;
  error_summary?: string | null;
  error_classification?: string | null;
  requested_by_user_id?: string | null;
  created_at: string;
  updated_at: string;
  finished_at?: string | null;
}

export interface MediaTranscriptionAnalysis {
  id: string;
  media_id: string;
  ai_run_id: string;
  version: number;
  is_active: boolean;
  requested_by_user_id?: string | null;
  created_at: string;
  updated_at: string;
  // Schemaless on the FE — read what you need, ignore the rest. The agent's
  // output shape may evolve (see definitions/agents/streamer_pitch_analyzer_v1).
  payload: Record<string, any>;
}

export interface MediaTranscriptionProductItem {
  codes: string[];
  label: string;
  original_label?: string;
  price?: number;
  original_price?: number;
  quantity?: number;
  is_hidden: boolean;
  origin: 'ai' | 'user';
  confidence?: number;
  messages_ids?: string[];
  audit_status?: string;
  mention_count?: number;
}

export interface MediaTranscriptionProducts {
  id: string;
  media_id: string;
  ai_run_id: string;
  version: number;
  is_active: boolean;
  is_user_modified: boolean;
  requested_by_user_id?: string | null;
  created_at: string;
  updated_at: string;
  last_reset_at?: string | null;
  products: MediaTranscriptionProductItem[];
}

export interface AnalyzeTranscriptionRequest {
  media_id: string;
}

export interface AnalyzeTranscriptionResponse {
  job_id: string;
  queue_position: number;
  status: 'analyzing';
}

// Sentinel error codes returned by POST /plugins/transcription/analyze
// when the kickoff is rejected. Stable tokens (not the human message).
export type AnalyzeTranscriptionErrorCode =
  | 'analysis_limit_reached'
  | 'analysis_requires_product_edit';
